import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { GlowCard } from "@/components/shared/GlowCard";
import { InteractiveCard } from "@/components/ui/interactive-card";

import { Shield, FileText, Users, TrendingUp, Check, AlertCircle, Home, Building2, HeartPulse, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";

const Rechtsschutz = () => {
  const stats = [
    { icon: Shield, value: "bis 500k €", label: "Versicherungssumme" },
    { icon: FileText, value: "50+", label: "Rechtsgebiete" },
    { icon: Users, value: "85%", label: "Arztpraxen" },
    { icon: TrendingUp, value: "ab 400 €", label: "Jahresbeitrag" }
  ];

  return <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />
      
      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/weitere-versicherungen">Weitere Versicherungen</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Rechtsschutzversicherung</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-accent/5 -z-10" />
          <ScrollAnimatedSection>
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-accent text-background">Wichtig</Badge>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words"
              >
                Rechtsschutzversicherung für Ärzte
              </motion.h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Umfassender rechtlicher Schutz für Ihre Praxis
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {stats.map((stat, idx) => (
                <GlowCard key={idx} to="#" index={idx}>
                  <stat.icon className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
                  <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
                </GlowCard>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/[0.02] to-background" />
        <ResponsiveSection spacing="lg">
          <ScrollAnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
              Leistungsübersicht
            </h2>
            <p className="text-sm text-muted-foreground mb-12 text-center">
              Umfassender Schutz in allen Rechtsgebieten
            </p>

            <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
              
              <div className="relative pb-6 md:pb-0">
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                    <AlertCircle className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                    <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                      Abgedeckte Rechtsgebiete
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                    Schutz in allen wichtigen Bereichen
                  </p>
                  
                  <div className="list-container">
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Arbeitsrecht (Streitigkeiten mit Mitarbeitern)</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Vertragsrecht (Lieferanten, Vermieter)</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Steuerrecht (Finanzamt)</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Sozialrecht (KV-Streitigkeiten)</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Verwaltungsrecht (Approbation, Zulassung)</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Zivilrecht (allgemeine Rechtsstreitigkeiten)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                    <FileText className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                    <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                      Besondere Leistungen
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                    Was die Versicherung leistet
                  </p>
                  
                  <div className="list-container">
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Freie Anwaltswahl in allen Rechtsgebieten</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Telefonische Rechtsberatung inkludiert</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Mediation und außergerichtliche Streitbeilegung</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Übernahme von Gerichts- und Anwaltskosten</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Weltweiter Schutz möglich</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Schnelle Deckungszusage innerhalb 48h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>
        </ResponsiveSection>
      </section>

      {/* Besondere Vorteile */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-accent/10" />
        <ResponsiveSection spacing="lg">
          <ScrollAnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
              Besondere Vorteile
            </h2>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glas-cards"
            >
              <div className="grid md:grid-cols-2 gap-6">
                
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Freie Anwaltswahl</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Sie wählen Ihren Anwalt frei - keine Vorgaben</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Telefonische Beratung</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Kostenlose Rechtsberatung am Telefon jederzeit</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Mediation inklusive</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Außergerichtliche Streitbeilegung mitversichert</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Keine Selbstbeteiligung</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">In vielen Tarifen komplett ohne Eigenanteil</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Schnelle Deckungszusage</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Innerhalb von 48 Stunden nach Prüfung</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Hohe Deckungssumme</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Bis zu 500.000 € pro Rechtsstreit</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </ScrollAnimatedSection>
        </ResponsiveSection>
      </section>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center break-words">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Für wen ist diese Versicherung geeignet?
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Unverzichtbar für jeden Praxisbetrieb
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50">
            <CardContent className="pt-6">
              <Home className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Niedergelassene Ärzte</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Besonders wichtig bei Angestellten</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Viele Vertragspartner</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Komplexe Rechtslage</span>
                </li>
              </ul>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50">
            <CardContent className="pt-6">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Gemeinschaftspraxen & MVZ</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Erhöhtes Risiko durch Komplexität</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Viele Vertragsstrukturen</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Mehrere Partner</span>
                </li>
              </ul>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50">
            <CardContent className="pt-6">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Praxisgründer</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Schutz ab dem ersten Tag</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Viele Verträge bei Gründung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Rechtliche Unsicherheiten</span>
                </li>
              </ul>
            </CardContent>
          </InteractiveCard>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Echte Schadensbeispiele
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Reale Fälle zeigen die Notwendigkeit
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)]">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Kosten: 12.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Arbeitsrecht: MFA-Kündigung</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Anfechtung einer Kündigung durch ehemalige MFA. Langwieriges Gerichtsverfahren.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)]">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Kosten: 8.500 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">KV-Wirtschaftlichkeitsprüfung</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Regress-Streit mit der Kassenärztlichen Vereinigung über Budgetüberschreitung.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Anwalts- und Gerichtskosten</span>
              </div>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)]">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Kosten: 18.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Mietrechtsstreit</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Streit über Nebenkostenabrechnungen und Renovierungspflichten bei Praxisauszug.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)]">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Kosten: 6.500 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Geräte-Lieferstreit</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Rechtstreit über defekte medizinische Geräte und Schadenersatzansprüche gegen Lieferanten.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Inkl. Gutachterkosten</span>
              </div>
            </CardContent>
          </InteractiveCard>
        </div>
      </ResponsiveSection>

      {/* NEUER CTA-Bereich mit Lead-Formular */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/15 via-accent/5 to-accent/15" />
        
        <motion.div 
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
        />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold mb-4">
                Jetzt persönlich beraten lassen
              </h2>
              <p className="text-muted-foreground mb-6">
                Unsere Experten helfen Ihnen, den optimalen Schutz zu finden.
              </p>
              <Link 
                to="/termin-buchen"
                className="inline-flex items-center px-8 py-4 bg-transparent text-foreground border-2 border-accent rounded-md font-semibold transition-all hover:shadow-[0_0_25px_rgba(139,195,74,0.5)] hover:scale-105"
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            
            <div className="bg-card/80 backdrop-blur-sm border border-accent/30 rounded-xl p-6 hover:border-accent/50 transition-colors">
              <h3 className="text-xl font-semibold mb-4 text-center">
                Oder hinterlassen Sie uns Ihre Daten
              </h3>
              <ContactForm />
            </div>
            
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </div>;
};
export default Rechtsschutz;
